// Bildekomprimering — to profiler
// storage: Høy kvalitet for brukerens arkiv, rimelig filstørrelse
// share:   Optimalisert for deling/fiskekort — skarpere, mindre, raskere lasting

const PROFILES = {
  storage: { maxDimension: 1600, quality: 0.82, maxSizeKB: 500 },
  share:   { maxDimension: 1200, quality: 0.78, maxSizeKB: 300 },
};

// Maks lengste side ved normalisering. Nyere iPhone (f.eks. 17 Pro) tar bilder
// på 48 MP (≈8000×6000). Lerret/minne i iOS Safari klarer ikke så store bilder,
// noe som gir «hvit skjerm». Vi skalerer derfor ned med en gang ved innlesing.
const MAX_NORMALIZE_DIMENSION = 2400;

// Last et bilde fra en URL (object-URL eller base64) til et HTMLImageElement.
// Avviser hvis bildet ikke kan dekodes, slik at vi aldri henger på en Promise.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Kunne ikke dekode bildet'));
    img.src = src;
  });
}

// Normaliser et kamerabilde før visning/lagring.
// Tar imot en File/Blob eller en base64-streng, skalerer ned store bilder og
// re-koder alltid til JPEG. Dette løser «hvit skjerm» på nyere iPhone der
// råbildet (48 MP eller HEIC) er for stort til å vises eller tegnes på lerret.
export async function normalizeImage(fileOrDataUrl, maxDimension = MAX_NORMALIZE_DIMENSION) {
  // Bruk object-URL for File/Blob — mer minnevennlig enn base64 for store filer.
  let objectUrl = null;
  let src;
  if (typeof fileOrDataUrl === 'string') {
    src = fileOrDataUrl;
  } else {
    objectUrl = URL.createObjectURL(fileOrDataUrl);
    src = objectUrl;
  }

  try {
    const img = await loadImage(src);
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;

    if (!width || !height) throw new Error('Bildet har ugyldige mål');

    // Skaler ned slik at lengste side ikke overstiger maxDimension
    if (width > maxDimension || height > maxDimension) {
      if (width > height) {
        height = Math.round((height / width) * maxDimension);
        width = maxDimension;
      } else {
        width = Math.round((width / height) * maxDimension);
        height = maxDimension;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    // Hvit bakgrunn i tilfelle kildebildet har gjennomsiktighet
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', 0.9);
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

export function compressImage(base64Image, profile = 'storage') {
  const { maxDimension, quality: startQuality, maxSizeKB } = PROFILES[profile] || PROFILES.storage;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      // Behold aspect ratio, skaler ned hvis nødvendig
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      let quality = startQuality;
      let compressedImage = canvas.toDataURL('image/jpeg', quality);

      // Reduser kvalitet til vi er under maxSizeKB
      while (compressedImage.length > maxSizeKB * 1024 && quality > 0.45) {
        quality -= 0.05;
        compressedImage = canvas.toDataURL('image/jpeg', quality);
      }

      console.log(`Bilde komprimert (${profile}): ${(base64Image.length / 1024).toFixed(0)}KB → ${(compressedImage.length / 1024).toFixed(0)}KB (${width}×${height}, kvalitet: ${(quality * 100).toFixed(0)}%)`);
      resolve(compressedImage);
    };
    img.onerror = () => {
      // Kunne ikke dekode — returner originalen så vi ikke henger på Promise
      console.error('compressImage: kunne ikke dekode bildet, beholder original');
      resolve(base64Image);
    };
    img.src = base64Image;
  });
}
